/**
 * 將一個逗號分隔的 key-value 字串 (例如 "key1=value1,key2=value2")
 * 解析並添加到指定的 URL 的查詢參數中。
 *
 * @param urlString 原始的 URL 字串。
 * @param paramsString 要添加的參數字串，格式為 "key1=value1,key2=value2,..."。
 * @returns 返回帶有新參數的 URL 字串，如果輸入的 URL 無效則返回原始 URL。
 */
export function addQueryParamsFromString(urlString: string, paramsString: string): string {
    // 檢查 paramsString 是否為空或僅包含空白字符
    if (!paramsString || paramsString.trim() === '') {
      return urlString;
    }
  
    try {
      // 建立 URL 物件來處理 URL 的各個部分
      // 這會自動處理現有的查詢參數和 URL 片段 (#)
      const url = new URL(urlString);
  
      // 分割參數字串
      const pairs = paramsString.split(',');
  
      pairs.forEach(pair => {
        // 去除每個鍵值對前後的空白
        const trimmedPair = pair.trim();
        // 如果是空字串則跳過
        if (!trimmedPair) {
          return;
        }
  
        // 使用 '=' 分割鍵和值
        // 只取第一個 '='，這樣值本身可以包含 '='
        const delimiterIndex = trimmedPair.indexOf('=');
  
        if (delimiterIndex !== -1) {
          // 提取鍵和值，並去除它們各自前後的空白
          const key = trimmedPair.substring(0, delimiterIndex).trim();
          const value = trimmedPair.substring(delimiterIndex + 1).trim();
  
          // 確保 key 不是空字串
          if (key) {
            // 使用 append 方法添加參數
            // 如果已有同名參數，append 會添加新的，而不是覆蓋
            // 如果需要覆蓋，可以使用 url.searchParams.set(key, value);
            url.searchParams.append(key, value);
          }
        } else {
          // 如果鍵值對沒有 '='，可以選擇忽略它，或者將其視為只有鍵的參數
          // 這裡選擇忽略格式不正確的鍵值對
          console.warn(`Skipping invalid pair: "${trimmedPair}"`);
        }
      });
  
      // 將更新後的 URL 物件轉換回字串
      return url.toString();
  
    } catch (error) {
      // 如果傳入的 urlString 無效，URL 建構子會拋出錯誤
      console.error(`Invalid URL provided: "${urlString}"`, error);
      // 在發生錯誤時返回原始 URL 字串
      return urlString;
    }
  }
  
//   // --- 範例使用 ---
  
//   const originalUrl1 = "https://www.example.com/path";
//   const paramsToAdd1 = "gaga=124124,asdf=123";
//   const newUrl1 = addQueryParamsFromString(originalUrl1, paramsToAdd1);
//   console.log(newUrl1); // 輸出: https://www.example.com/path?gaga=124124&asdf=123
  
//   const originalUrl2 = "https://www.example.com/path?existing=true";
//   const paramsToAdd2 = " name = John Doe , age = 30 "; // 包含空白
//   const newUrl2 = addQueryParamsFromString(originalUrl2, paramsToAdd2);
//   console.log(newUrl2); // 輸出: https://www.example.com/path?existing=true&name=John+Doe&age=30 (空白會被編碼)
  
//   const originalUrl3 = "https://www.example.com/path#section";
//   const paramsToAdd3 = "user=admin,mode=edit";
//   const newUrl3 = addQueryParamsFromString(originalUrl3, paramsToAdd3);
//   console.log(newUrl3); // 輸出: https://www.example.com/path?user=admin&mode=edit#section (片段會被保留在最後)
  
//   const originalUrl4 = "invalid-url";
//   const paramsToAdd4 = "a=1";
//   const newUrl4 = addQueryParamsFromString(originalUrl4, paramsToAdd4);
//   console.log(newUrl4); // 輸出: invalid-url (並在控制台打印錯誤)
  
//   const originalUrl5 = "https://www.example.com";
//   const paramsToAdd5 = "a=1,,b=2, invalidPair , c = 3=4 "; // 包含空段、無效段、值包含=
//   const newUrl5 = addQueryParamsFromString(originalUrl5, paramsToAdd5);
//   console.log(newUrl5); // 輸出: https://www.example.com/?a=1&b=2&c=3%3D4 (控制台會警告 invalidPair)

/**
 * Generates a normalized URL string containing only whitelisted query parameters,
 * sorted alphabetically by key.
 *
 * @param urlString The original URL string.
 * @param whitelist An array of query parameter keys to keep.
 * @returns The normalized URL string with only sorted, whitelisted parameters,
 * or the original URL string if it's invalid.
 */
export function normalizeUrlForWhitelistedParams(urlString: string, whitelist: string[]): string {
    try {
        const url = new URL(urlString);
        const originalParams = url.searchParams;
        const whitelistedParams: [string, string][] = [];

        // 1. Filter parameters based on the whitelist
        for (const key of whitelist) {
            // Check if the parameter exists in the original URL
            if (originalParams.has(key)) {
                // Get all values if a key appears multiple times (though typically not needed for IDs like in the example)
                // For simplicity here, we'll just get the first value using get().
                // If multiple values for the same key need to be preserved, use getAll() and handle accordingly.
                const value = originalParams.get(key);
                if (value !== null) { // Should always be true if .has() is true, but good practice
                    whitelistedParams.push([key, value]);
                }
            }
        }

        // 2. Sort the filtered parameters alphabetically by key
        whitelistedParams.sort((a, b) => a[0].localeCompare(b[0]));

        // 3. Rebuild the search parameters string
        const newSearchParams = new URLSearchParams();
        for (const [key, value] of whitelistedParams) {
            newSearchParams.append(key, value);
        }
        const newQueryString = newSearchParams.toString();

        // 4. Construct the final URL
        // Get the base part of the URL (protocol, host, pathname)
        const baseUrl = `${url.protocol}//${url.host}${url.pathname}`;

        // Append the new query string only if it's not empty
        if (newQueryString) {
            return `${baseUrl}?${newQueryString}`;
        } else {
            return baseUrl; // Return base URL if no whitelisted params were found or provided
        }

    } catch (error) {
        // Handle invalid URL inputs
        console.error(`Error parsing URL: ${urlString}`, error);
        // Return the original string or throw an error, based on desired behavior
        return urlString;
    }
}

// // --- 範例使用 ---

// const whitelist = ["act", "q", "id"];

// const url1 = "https://running.biji.co/index.php?act=info&q=news&id=111911&asdf=123&tracking=124";
// const url2 = "https://running.biji.co/index.php?q=news&id=111911&asdf=123&tracking=124&act=info";
// const url3 = "https://running.biji.co/index.php?id=111911&act=info&q=news"; // Already sorted differently
// const url4 = "https://running.biji.co/index.php?tracking=555"; // No whitelisted params
// const url5 = "https://running.biji.co/index.php"; // No query string at all

// const normalizedUrl1 = normalizeUrlForWhitelistedParams(url1, whitelist);
// const normalizedUrl2 = normalizeUrlForWhitelistedParams(url2, whitelist);
// const normalizedUrl3 = normalizeUrlForWhitelistedParams(url3, whitelist);
// const normalizedUrl4 = normalizeUrlForWhitelistedParams(url4, whitelist);
// const normalizedUrl5 = normalizeUrlForWhitelistedParams(url5, whitelist);

// console.log(`Original: ${url1}`);
// console.log(`Normalized: ${normalizedUrl1}`); // 輸出: https://running.biji.co/index.php?act=info&id=111911&q=news

// console.log(`Original: ${url2}`);
// console.log(`Normalized: ${normalizedUrl2}`); // 輸出: https://running.biji.co/index.php?act=info&id=111911&q=news

// console.log(`Original: ${url3}`);
// console.log(`Normalized: ${normalizedUrl3}`); // 輸出: https://running.biji.co/index.php?act=info&id=111911&q=news

// console.log(`Original: ${url4}`);
// console.log(`Normalized: ${normalizedUrl4}`); // 輸出: https://running.biji.co/index.php

// console.log(`Original: ${url5}`);
// console.log(`Normalized: ${normalizedUrl5}`); // 輸出: https://running.biji.co/index.php